import React, { FC } from 'react';

import noop from 'lodash/noop';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { TableBody } from 'components/DataDisplay/NewTable/TableBody';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';

import { NestedPagerRow } from './NestedPagerRow';
import { NestedStatusRow } from './NestedStatusRow';
import { TableRows } from './TableRows';
import { ROW_CELL_STYLES } from './useDataAccessColumns';
import {
  DataAccessIdentity,
  useDataAccessSchemas,
} from './useDataAccessQueries';
import { DataAccessViewActions } from './useDataAccessView';
import {
  DataAccessRow,
  DataAccessViewState,
  getErrorMessage,
  toSchemaRow,
} from './utils';

interface SchemaRowsProps {
  identity: DataAccessIdentity;
  database: DataAccessRow;
  view: DataAccessViewState;
  actions: DataAccessViewActions;
  columns: TableColumnProps<DataAccessRow>[];
}

/**
 * One page of an open database's schemas, read live, with its own pager. An
 * open schema shows its tables under it.
 */
export const SchemaRows: FC<SchemaRowsProps> = props => {
  const { identity, database, view, actions, columns } = props;
  const page = view.schemaPages[database.uuid] ?? INIT_PAGE_INFO;
  const query = useDataAccessSchemas(
    identity,
    database.uuid,
    view.filters,
    page,
  );
  const { data } = query;

  if (query.isError && !data)
    return (
      <NestedStatusRow
        depth={1}
        error={getErrorMessage(query.error, 'Failed to load schemas')}
        onRetry={() => query.refetch()}
      />
    );
  if (!data) return <NestedStatusRow depth={1} isLoading />;

  const sensitivityAvailable = data.sensitivity_available ?? true;
  const rows = (data.items ?? []).map(schema =>
    toSchemaRow(
      schema,
      view.openSchemas.includes(schema.uuid),
      sensitivityAvailable,
    ),
  );
  const total = data.total ?? 0;

  return (
    <>
      {rows.length ? (
        // The table's own row component, so these rows match the database rows.
        rows.map((row, index) => (
          <TableBody
            key={row.uuid}
            columns={columns}
            row={row}
            index={index}
            rowIndex={-1}
            setCurrentRowIndex={noop}
            isRowActive={undefined}
            cursor={row.canOpen ? 'pointer' : 'default'}
            onRowClick={schema =>
              schema.canOpen && actions.toggleSchema(schema.uuid)
            }
            styles={{
              cell: ROW_CELL_STYLES,
              row: { opacity: query.isPlaceholderData ? 0.5 : 1 },
            }}
            expanded
            subComponent={({ row: schema }) =>
              schema.isOpen ? (
                <TableRows
                  identity={identity}
                  schema={schema}
                  view={view}
                  actions={actions}
                  columns={columns}
                />
              ) : null
            }
          />
        ))
      ) : (
        <NestedStatusRow depth={1} message="No schemas match these filters" />
      )}
      {/* A failed refresh keeps the rows it had, with the error under them. */}
      {query.isError && (
        <NestedStatusRow
          depth={1}
          error={getErrorMessage(query.error, 'Failed to load schemas')}
          onRetry={() => query.refetch()}
        />
      )}
      {(total > page.page_size || page.page_number > 1) && (
        <NestedPagerRow
          total={total}
          pageInfo={page}
          label={`schemas in ${database.name}`}
          isFetching={query.isFetching}
          onChange={info => actions.setSchemaPage(database.uuid, info)}
        />
      )}
    </>
  );
};
